import type { Variants } from 'framer-motion';
import { TIMING, COUNT_LEAD_MS as _COUNT_LEAD_MS, SETTLE_EASE } from './animationConfig';

// All timing knobs live in animationConfig.ts (TIMING) — this module only SHAPES them into variants,
// the ECharts animation block, and the stagger functions. Re-export the raw values components already
// import by name, so the single source of truth is animationConfig without churning every import site.
export const FRAME_BEAT_MS = TIMING.frameBeatMs;
export const SERIES_LEAD_MS = TIMING.seriesLeadMs;
export const MARK_SWEEP_MS = TIMING.markSweepMs;
export const COUNT_READUP_SEC = TIMING.countReadUpSec;
export const COUNT_HOLD_MS = TIMING.countHoldMs;
export const COUNT_LEAD_MS = _COUNT_LEAD_MS;

// Card entrance: subtle rise + fade. Stagger children with delayChildren/staggerChildren on a parent.
export const cardEntrance: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: TIMING.cardEntranceSec, ease: SETTLE_EASE } },
};

export const listStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: TIMING.listStaggerSec } },
};

/**
 * Chart card entrance — DISTINCT from cardEntrance: opacity settles fast so the ECharts canvas is opaque
 * almost immediately and the staged in-chart draw plays on a SETTLED card instead of being washed out by
 * a slow card-level fade. The chart's "part by part" build is the show; the card just needs to arrive.
 */
export const chartReveal: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: TIMING.chartRevealSec, ease: SETTLE_EASE } },
};

/**
 * The chart CARD's slow, cinematic materialize (blur + rise + fade) — used when the empty card appears as
 * its OWN step in the sequence, before the data draws inside it. Heavier than chartReveal on purpose.
 */
export const cardSlowReveal: Variants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(8px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: TIMING.cardRevealSec, ease: SETTLE_EASE } },
};

// Honest in-chart animation — no bounce/overshoot on data. One slow cubicOut pass. `animationDelay` is
// the FRAME BEAT: every series that doesn't set its own delay (e.g. the stacked-area bands) waits this
// long so the empty framed grid registers before any data draws — "frame first, then the parts".
export const ECHARTS_ANIM = {
  animationDuration: TIMING.seriesDrawMs,
  animationEasing: 'cubicOut' as const,
  animationDelay: FRAME_BEAT_MS,
};

/**
 * "Outline first, then the rest" — a line-family chart builds as ordered layers, not one simultaneous
 * draw. The framed grid/axes paint at t=0; FRAME_BEAT_MS later the PRIMARY series draws (the outline of
 * the data), and each SECONDARY layer (rolling average, trend overlay) joins one LEAD behind the one
 * before it. The discrete-mark charts use the per-mark sweeps below; both are frame-led off FRAME_BEAT.
 */
export const seriesLead = (order: number): number => FRAME_BEAT_MS + order * SERIES_LEAD_MS;

/**
 * Per-mark stagger for discrete-mark series (bars, bubbles). Marks draw left→right as a single honest
 * pass, FRAME-LED: each mark starts at `FRAME_BEAT_MS + idx * min(markStepCap, MARK_SWEEP/n)`, so the
 * empty grid registers first and the whole sweep spans ~MARK_SWEEP_MS without crawling on wide series.
 * When the series is dense (`n > 80`) the per-mark offset would crawl, so the marks fade in together
 * AFTER the frame beat (a constant `FRAME_BEAT_MS`). `n` is the series length captured by the caller in
 * its builder closure — NOT read from the ECharts callback params.
 */
export function markStagger(n: number): number | ((idx: number) => number) {
  if (n > 80) return FRAME_BEAT_MS;
  const step = Math.min(TIMING.markStepCapMs, MARK_SWEEP_MS / n);
  return (idx: number) => FRAME_BEAT_MS + idx * step;
}

/**
 * Point-cloud stagger for scatter/bubble. UNLIKE markStagger, this NEVER collapses for dense series — for
 * a scatter cloud the staggered "materialize" IS the animation, and a real cohort scatter is usually dense
 * (>80 points). Frame-led, with the per-point step capped so the whole sweep stays ~MARK_SWEEP_MS even for
 * a few hundred points. Honest: only the REVEAL ORDER is staggered; every point's position is fixed. `n`
 * is the point count captured in the builder closure.
 */
export function pointStagger(n: number): (idx: number) => number {
  const step = n > 0 ? Math.min(20, MARK_SWEEP_MS / n) : 0;
  return (idx: number) => FRAME_BEAT_MS + idx * step;
}

/**
 * Cell-opacity stagger for the calendar heatmap. Cells fade in across the grid, frame-led, with the sweep
 * (past the beat) capped at MARK_SWEEP_MS regardless of how many days are present — past that, each cell's
 * delay is clamped so the last cells don't lag. `n` is the cell count captured in the builder closure.
 */
export function cellStagger(n: number): (idx: number) => number {
  const step = n > 0 ? MARK_SWEEP_MS / n : 0;
  return (idx: number) => FRAME_BEAT_MS + Math.min(MARK_SWEEP_MS, idx * step);
}

/**
 * Recursively force `animation:false` everywhere in an arbitrary ECharts option. The catalog charts all
 * render through `ResponsiveECharts`, which applies this when reduced motion is preferred; the escape
 * hatch (a model-authored option whose sanitizer leaves nested per-series `animation` keys) applies it
 * directly. Returns a new option; the input is untouched.
 */
export function stripEchartsAnimation<T>(option: T): T {
  if (Array.isArray(option)) {
    return option.map((v) => stripEchartsAnimation(v)) as unknown as T;
  }
  if (option && typeof option === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(option as Record<string, unknown>)) {
      // Drop any animation* key (animation, animationDuration, animationDelay, …) so no nested series
      // re-enables a tween, then pin animation:false at this level.
      if (/^animation/i.test(k)) continue;
      out[k] = stripEchartsAnimation(v);
    }
    out.animation = false;
    return out as T;
  }
  return option;
}
